'use client'

import { useEffect, useState } from 'react'
import { useHMRCITSAStore } from '@repo/store/useHMRCITSAStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { confirm } from '@repo/ui/Confirm'
import { Drawer } from '@repo/ui/Drawer'
import type { HmrcMtdItsaCategory, HmrcMtdItsaCategoryMapping, NominalAccount } from '../types'
import { CategoryMappingForm } from './CategoryMappingForm'
import { NOMINAL_ACCOUNT_TYPE } from './constants'
import MUIAlert from '@mui/material/Alert'
import MUIBox from '@mui/material/Box'
import MUIButton from '@mui/material/Button'
import MUICircularProgress from '@mui/material/CircularProgress'
import MUIDivider from '@mui/material/Divider'
import MUIStack from '@mui/material/Stack'
import MUITab from '@mui/material/Tab'
import MUITabs from '@mui/material/Tabs'
import MUITypography from '@mui/material/Typography'

type DrawerState = { categoryId: number; mapping: HmrcMtdItsaCategoryMapping | null } | null

const GENERIC_LOAD_ERROR = 'An error occurred while loading category data. Please try again.'

// The HMRC ITSA category-mapping configuration screen (FE6, route wired by FE11). Lets a
// supervisor tell the calculation engine which Osprey data (fee income, or a specific
// nominal account) feeds each DM4-seeded HMRC MTD ITSA category — see the ITSA frontend
// guide, section 7. Categories are read-only (BE1); mappings are full CRUD (BE1) through
// the FE3 store; the nominal picker is fed from the Lookups (LK1) account list.
export const CategoryMappingModule = () => {
  const { getHmrcCategories, getCategoryMappings, getAllAccountsByType, deleteCategoryMapping } = useHMRCITSAStore()
  // Estate pattern (see CaseTypeMappingModule): self-supplied top clearance when the layout
  // hides the PageTitleBlock on reduced-height windows (the breadcrumb bar is position:fixed).
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()

  const [tabValue, setTabValue] = useState(0)
  const [quarterlyCategories, setQuarterlyCategories] = useState<HmrcMtdItsaCategory[]>([])
  const [annualCategories, setAnnualCategories] = useState<HmrcMtdItsaCategory[]>([])
  const [mappings, setMappings] = useState<HmrcMtdItsaCategoryMapping[]>([])
  const [nominalAccounts, setNominalAccounts] = useState<NominalAccount[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [drawerState, setDrawerState] = useState<DrawerState>(null)

  const categories = tabValue === 0 ? quarterlyCategories : annualCategories

  const loadAll = async () => {
    setLoadError('')
    setIsLoading(true)
    try {
      const [quarterly, annual, allMappings, accounts] = await Promise.all([
        getHmrcCategories({ isAnnual: false }),
        getHmrcCategories({ isAnnual: true }),
        getCategoryMappings(),
        getAllAccountsByType({ accountType: NOMINAL_ACCOUNT_TYPE })
      ])
      setQuarterlyCategories(quarterly || [])
      setAnnualCategories(annual || [])
      setMappings(allMappings || [])
      setNominalAccounts(accounts || [])
    } catch (err) {
      setLoadError((err as Error)?.message || GENERIC_LOAD_ERROR)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refreshMappings = async () => {
    const allMappings = await getCategoryMappings()
    setMappings(allMappings || [])
  }

  const handleAdd = (categoryId: number) => setDrawerState({ categoryId, mapping: null })
  const handleEdit = (mapping: HmrcMtdItsaCategoryMapping) =>
    setDrawerState({ categoryId: mapping.hmrcMtdItsaCategoryId, mapping })
  const handleCloseDrawer = () => setDrawerState(null)

  const handleSaved = async () => {
    setDrawerState(null)
    await refreshMappings()
  }

  const handleDelete = async (mapping: HmrcMtdItsaCategoryMapping) => {
    const proceed = await confirm('Are you sure you want to delete this mapping?')
    if (!proceed) return
    await deleteCategoryMapping({ hmrcMtdItsaCategoryMappingId: mapping.hmrcMtdItsaCategoryMappingId })
    await refreshMappings()
  }

  const activeCategory = drawerState
    ? [...quarterlyCategories, ...annualCategories].find((c) => c.hmrcMtdItsaCategoryId === drawerState.categoryId)
    : undefined

  return (
    <MUIStack
      data-testid="CategoryMappingModule"
      gap={4}
      sx={{
        maxWidth: 900,
        margin: 'auto',
        mt: pageTitleBlockIsVisible ? 'auto' : '12rem',
        backgroundColor: 'white',
        borderRadius: '6px',
        px: '40px',
        pb: '60px',
        pt: '40px'
      }}
    >
      <MUITypography variant="h5">HMRC ITSA Category Mapping</MUITypography>
      <MUITypography variant="body1">
        Configure which Osprey nominal accounts, and fee income, map to each HMRC MTD ITSA category. These mappings
        are used when calculating cumulative period summaries from your ledger.
      </MUITypography>

      {loadError && (
        <MUIAlert severity="error" data-testid="CategoryMappingModuleLoadError">
          {loadError}
        </MUIAlert>
      )}

      <MUIBox sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <MUITabs data-testid="CategoryMappingModuleTabs" value={tabValue} onChange={(_, val) => setTabValue(val)}>
          <MUITab label="Quarterly Categories" />
          <MUITab label="Annual Categories" />
        </MUITabs>
      </MUIBox>

      {isLoading ? (
        <MUIStack alignItems="center" sx={{ padding: 4 }}>
          <MUICircularProgress />
        </MUIStack>
      ) : (
        <MUIStack gap={3}>
          {categories.length === 0 ? (
            <MUITypography variant="body2" sx={{ color: 'text.secondary' }}>
              No categories found.
            </MUITypography>
          ) : (
            categories.map((category) => {
              const categoryMappings = mappings.filter(
                (mapping) => mapping.hmrcMtdItsaCategoryId === category.hmrcMtdItsaCategoryId
              )

              return (
                <MUIStack key={category.hmrcMtdItsaCategoryId} data-testid="CategoryMappingModuleCategory" gap={1.5}>
                  <MUIStack direction="row" alignItems="center" justifyContent="space-between">
                    <MUITypography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {category.name}
                    </MUITypography>
                    <MUIButton
                      size="small"
                      variant="outlined"
                      data-testid={`CategoryMappingModuleAddButton-${category.hmrcMtdItsaCategoryId}`}
                      onClick={() => handleAdd(category.hmrcMtdItsaCategoryId)}
                    >
                      + Add mapping
                    </MUIButton>
                  </MUIStack>

                  {categoryMappings.length === 0 && (
                    <MUITypography variant="body2" sx={{ color: 'text.secondary', pl: 1 }}>
                      No mappings configured.
                    </MUITypography>
                  )}

                  {categoryMappings.map((mapping) => (
                    <MUIStack
                      key={mapping.hmrcMtdItsaCategoryMappingId}
                      data-testid={`CategoryMappingModuleRow-${mapping.hmrcMtdItsaCategoryMappingId}`}
                      direction="row"
                      alignItems="center"
                      gap={2}
                      sx={{ px: 2, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: '4px' }}
                    >
                      <MUIStack direction="row" gap={2} sx={{ flexGrow: 1, flexWrap: 'wrap' }}>
                        <MUITypography variant="body2" sx={{ color: 'text.secondary', minWidth: 80 }}>
                          {mapping.sourceType}
                        </MUITypography>
                        {mapping.nominalCode && (
                          <MUITypography variant="body2" sx={{ color: 'text.secondary', minWidth: 100 }}>
                            {mapping.nominalCode}
                          </MUITypography>
                        )}
                        <MUITypography variant="body2">{mapping.description}</MUITypography>
                      </MUIStack>

                      <MUIStack direction="row" gap={1}>
                        <MUIButton
                          size="small"
                          variant="outlined"
                          data-testid={`CategoryMappingModuleEditButton-${mapping.hmrcMtdItsaCategoryMappingId}`}
                          onClick={() => handleEdit(mapping)}
                        >
                          Edit
                        </MUIButton>
                        <MUIButton
                          size="small"
                          color="error"
                          variant="outlined"
                          data-testid={`CategoryMappingModuleDeleteButton-${mapping.hmrcMtdItsaCategoryMappingId}`}
                          onClick={() => handleDelete(mapping)}
                        >
                          Delete
                        </MUIButton>
                      </MUIStack>
                    </MUIStack>
                  ))}

                  <MUIDivider sx={{ mt: 1 }} />
                </MUIStack>
              )
            })
          )}
        </MUIStack>
      )}

      <Drawer
        isOpen={Boolean(drawerState)}
        onClose={handleCloseDrawer}
        headingNode={<MUITypography variant="h5">{drawerState?.mapping ? 'Edit Mapping' : 'Add Mapping'}</MUITypography>}
      >
        {drawerState && activeCategory && (
          <CategoryMappingForm
            category={activeCategory}
            mapping={drawerState.mapping}
            accounts={nominalAccounts}
            onCancel={handleCloseDrawer}
            onSaved={handleSaved}
          />
        )}
      </Drawer>
    </MUIStack>
  )
}
